import { Box, Fab, Grid, Stack, Typography } from '@mui/material'
import 'moment/locale/it'
import { forwardRef } from 'react'

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../app/hooks'
import { GenericListItems } from '../../components/GenericListItem'
import useHapticFeedback from '../../components/HapticFeedback'
import { PopupMessage } from '../../components/PopupMessage'
import SearchInput from '../../components/SearchInput'
import { ArtAnaItem } from '../ricerca/ArtAnaItem'
import { useGetListiniOfferteQuery } from '../ricerca/ricercaAPI'
import {
    ArtAna,
    PAGE_START,
    TIPO_RICHIESTA,
    selectQueryPaginationOfferte,
    setQueryPaginationOfferte,
} from '../ricerca/ricercaSlice'

const ArtAnaList = GenericListItems<ArtAna>()

const Loading = forwardRef<React.ComponentRef<'div'>, unknown>(function Loading(props, ref) {
    return (
        <div ref={ref} className="p-1">
            <div className="animate-pulse bg-slate-600 p-4 text-white">Caricamento...</div>
        </div>
    )
})

export function Offerte() {
    const dispatch = useDispatch()

    const queryPaginationOfferte = useAppSelector(selectQueryPaginationOfferte)

    const triggerVibration = useHapticFeedback()

    // --------------------------------------------------------------
    // INFINITE SCROLL
    // --------------------------------------------------------------

    const {
        data,
        isLoading,
        error: isErrorData,
    } = useGetListiniOfferteQuery({
        search: queryPaginationOfferte.search,
        page: queryPaginationOfferte.page,
        pageSize: queryPaginationOfferte.pageSize,
        filter: queryPaginationOfferte.filter,
        listino: queryPaginationOfferte.listino,
        history: queryPaginationOfferte.history,
    })

    const loadMore = async () => {
        dispatch(
            setQueryPaginationOfferte({
                ...queryPaginationOfferte,
                tipoRichiesta: TIPO_RICHIESTA.PAGINAZIONE,
                page: queryPaginationOfferte.page + 1,
            })
        )
        triggerVibration(20)
    }

    const [infiniteRef] = useInfiniteScroll({
        loading: isLoading,
        hasNextPage: queryPaginationOfferte.hasNextPage,
        onLoadMore: loadMore,
        // When there is an error, we stop infinite loading.
        // It can be reactivated by setting "error" state as undefined.
        disabled: Boolean(isErrorData),
        // `rootMargin` is passed to `IntersectionObserver`.
        // We can use it to trigger 'onLoadMore' when the sentry comes near to become
        // visible, instead of becoming fully visible on the screen.
        rootMargin: '0px 0px 40px 0px',
    })

    const handleSearchChange = (searchTerm: string) => {
        dispatch(
            setQueryPaginationOfferte({
                ...queryPaginationOfferte,
                hasNextPage: true,
                tipoRichiesta: TIPO_RICHIESTA.INIT,
                page: PAGE_START,
                search: searchTerm,
                data: null,
            })
        )
    }

    return (
        <Box>
            <PopupMessage codice="003" />
            <Stack
                direction="row"
                sx={{
                    pt: 1,
                    pb: 1,
                }}
                gap={1}
            >
                <SearchInput
                    initialValue={queryPaginationOfferte.search}
                    onSearchChange={handleSearchChange}
                    isLoading={isLoading}
                />
            </Stack>
            <Grid container spacing={2} textAlign="center">
                <Grid item xs={12}>
                    <Typography fontStyle="italic" variant="h6">
                        {data?.listini.listiniOfferte?.DES_TIPO_LIST}
                    </Typography>
                </Grid>
            </Grid>
            {queryPaginationOfferte.data != null && data != null && (
                <>
                    <ArtAnaList
                        isLoading={isLoading}
                        data={queryPaginationOfferte.data.data}
                        render={(artAna) => (
                            <ArtAnaItem
                                artUmList={artAna.artUmList}
                                carrelloAppoggio={artAna.carrelloAppoggio}
                                key={artAna.COD_ART}
                                _COD_ART={artAna.COD_ART}
                                _DES_RIGA={artAna.DES_ART}
                                _DES_CAT={artAna.DES_CAT}
                                _UM_BASE={artAna.UM_BASE}
                                _PREZZO_LISTINO={artAna.PREZZO_LISTINO}
                                _COD_LIST={artAna.COD_LIST}
                                _DEFAULT_COD_LIST={artAna.DEFAULT_COD_LIST}
                                _WEB_DISABILITATO={artAna.WEB_DISABILITATO}
                                _WEB_NOTA_1={artAna.WEB_NOTA_1}
                                _WEB_NOTA_2={artAna.WEB_NOTA_2}
                                _WEB_NOTA_3={artAna.WEB_NOTA_3}
                                _NO_VIS_CASSE={artAna.NO_VIS_CASSE}
                                _NO_VIS_KG={artAna.NO_VIS_KG}
                                _NO_VIS_PZ={artAna.NO_VIS_PZ}
                                _QTA_MIN_CASSE={artAna.QTA_MIN_CASSE}
                                _QTA_MIN_KG={artAna.QTA_MIN_KG}
                                _QTA_MIN_PZ={artAna.QTA_MIN_PZ}
                                listini={data.listini}
                            />
                        )}
                    />
                    {queryPaginationOfferte.hasNextPage && <Loading ref={infiniteRef} />}
                </>
            )}
            <Box sx={{ height: 50 }}></Box>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: '10%',
                    left: '10%',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'center',
                    width: 'auto',
                }}
            >
                <Fab
                    color="primary"
                    size="large"
                    onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                >
                    <ArrowUpwardIcon />
                </Fab>
            </Box>
        </Box>
    )
}
